import React, { useEffect, useRef, useState } from "react";

import { observer } from "mobx-react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { CircularProgress } from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

import { getCurrentFilterValues } from "utils/statistic";
import { helpUrl } from "configs/config";

import ZoomToGeo from "widgets/zoomToGeo/ZoomToGeo";

import DownloadPdfButton from "./DownloadPdfButton";
import CreateFactSheetButton from "./CreateFactSheetButton";
import FactSheetPanel from "./FactSheetPanel";
import InfoSections from "./InfoSections";
import TimeOverviewTabs from "./TimeOverviewTabs";

import { defaultNls, defaultTimeOverviewNls } from "./constants";

import useAutoOpenStatsWidget from "./hooks/useAutoOpenStatsWidget";
import useLayerDescription from "./hooks/useLayerDescription";
import useSelectionGeometry from "./hooks/useSelectionGeometry";
import useSelectionMeta from "./hooks/useSelectionMeta";
import useRasterStatisticsActions from "./hooks/useRasterStatisticsActions";
import useStatisticCharts from "./hooks/useStatisticCharts";

/**
 * RasterStatistics widget (container).
 *
 * Owns all behavior (store messaging, effects, fetching, chart/PDF generation)
 * and renders dumb UI components with minimal prop surfaces.
 */

const RasterStatistics = (props) => {
  const {
    infoTextAttributeId,
    nls = defaultNls,
    CommunicationStore,
    selectionUnits = ["Landkreis", "Naturräume"],
  } = props;

  const [resultTableLoading, setResultTableLoading] = useState(false);
  const [tableResult, setTableResult] = useState(null);
  const [tableError, setTableError] = useState(false);
  const [tab, setTab] = React.useState(1);

  useAutoOpenStatsWidget({
    CommunicationStore,
    resultTableLoading,
    tableResult,
  });

  // Use the active shown layer URL so the definition always matches what is rendered.
  const shownLayersMsg = CommunicationStore.getMessagesToID({
    toId: "shownLayers",
  })?.message;
  const shownEntry = shownLayersMsg?.layerInfos?.slice(-1)[0];
  const layerurl = shownEntry?.metaData?.url || "";

  const { description } = useLayerDescription({ layerurl });

  const { geometry } = useSelectionGeometry({ CommunicationStore });

  const {
    entityDisplayText,
    selectedEntity,
    units,
    currentChoice,
    decimalPlaces,
    hasPossibilities,
    // graphicTooLittle,
  } = useSelectionMeta({
    CommunicationStore,
    selectionUnits,
  });

  const { items = [], index: infoTextIndex } =
    CommunicationStore.getMessagesToID({
      to: "filterAccess",
    })?.message(infoTextAttributeId) || {};
  const { label = "" } = items[infoTextIndex] || {};
  const infoText = description;

  const mainTableResult = React.useMemo(() => {
    return Array.isArray(tableResult)
      ? tableResult
      : (tableResult && tableResult.mainResults) || [];
  }, [tableResult]);

  const aesTableResult = React.useMemo(() => {
    return Array.isArray(tableResult)
      ? []
      : (tableResult && tableResult.aesResults) || [];
  }, [tableResult]);

  const timeOverviewNls = defaultTimeOverviewNls;

  const { chart, aesChart } = useStatisticCharts({
    mainTableResult,
    aesTableResult,
    units,
    decimalPlaces,
  });

  const filters = getCurrentFilterValues(CommunicationStore) || {};
  const selectionMsg = CommunicationStore.getMessagesToID({
    to: "selectionGraphic",
  })?.message;

  const { Kategorie, Parameter, Zeitabschnitt } = filters;
  const ObjectId = selectionMsg?.entity?.ObjectId;
  const ServiceId = selectionMsg?.entity?.GisServiceId;

  const _mounted = useRef(false);
  useEffect(() => {
    if (!_mounted.current) {
      _mounted.current = true;
      return;
    }

    setTableResult(null);
    setTableError(false);
  }, [Kategorie, Parameter, Zeitabschnitt, ObjectId, ServiceId]);

  const { createFactSheet, createPDF } = useRasterStatisticsActions({
    CommunicationStore,
    decimalPlaces,
    nls,
    label,
    entityDisplayText,
    selectedEntity,
    infoText,
    mainTableResult,
    aesTableResult,
    timeOverviewNls,
    chart,
    aesChart,
    Zeitabschnitt,
    currentChoice,
    setTableResult,
    setResultTableLoading,
    setTableError,
  });

  const timespanLabel =
    Zeitabschnitt || (currentChoice && currentChoice.label) || "";
  const unitDisplay = units || "";
  const tabHeaderText = `${label || ""} [${unitDisplay}] – ${timespanLabel}`;

  const createFactSheetTooltip = !hasPossibilities
    ? nls.createFactSheetTooltipParameter
    : !geometry
    ? nls.createFactSheetTooltipRegion
    : "";

  const showInfoSections = Boolean(hasPossibilities && infoText);

  const tabsVisible = Boolean(hasPossibilities && tableResult);

  const downloadVisible = Boolean(
    tableResult && (mainTableResult?.length || aesTableResult?.length)
  );

  return (
    <Box style={{ marginTop: 10, marginLeft: 20, marginRight: 20 }}>
      <div
        style={{
          position: "absolute",
          right: 21,
          top: 16,
          zIndex: 1,
        }}
      >
        <a
          href={helpUrl + "/hauptfunktionen/informationsfeld"}
          target="Hilfe"
          title="Hilfe öffnen"
        >
          <FontAwesomeIcon
            style={{ width: "24px", height: "24px" }}
            icon={faQuestionCircle}
            color="grey"
          />
        </a>
      </div>

      <Typography variant="h5" style={{ marginBottom: 10 }}>
        {label}
      </Typography>

      <InfoSections visible={showInfoSections} infoText={infoText} nls={nls} />

      <Box style={{ marginTop: 10 }} id="faktenBlatt">
        <FactSheetPanel>
          <ZoomToGeo
            CommunicationStore={CommunicationStore}
            onChangeSelection={() => setTableResult(null)}
            // graphicTooLittle={graphicTooLittle}
          />

          <CreateFactSheetButton
            label={nls.createFactSheet}
            tooltip={createFactSheetTooltip}
            disabled={!hasPossibilities || !geometry || resultTableLoading}
            onClick={() => {
              createFactSheet();
            }}
          />

          {resultTableLoading && <CircularProgress />}

          {tabsVisible && (
            <TimeOverviewTabs
              nls={nls}
              tab={tab}
              onTabChange={(nextTab) => setTab(nextTab)}
              loading={resultTableLoading}
              error={tableError}
              tabHeaderText={tabHeaderText}
              tableNls={timeOverviewNls}
              mainTableResult={mainTableResult}
              aesTableResult={aesTableResult}
              chart={chart}
              aesChart={aesChart}
            />
          )}

          {hasPossibilities && (
            <hr style={{ marginTop: 15, marginBottom: 5 }} />
          )}

          <DownloadPdfButton
            visible={downloadVisible}
            label={nls.download}
            onDownloadPdf={() => {
              createPDF();
            }}
          />
        </FactSheetPanel>
      </Box>
    </Box>
  );
};

export default observer(RasterStatistics);
